/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, withAssign } from '@reatom/framework'

import { AsyncStateN, Iterable, pipe, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth/atoms'
import { CurrencyRef } from '@root/entities/currency/model'
import { selectedNetworkAtom } from '@root/entities/network'
import { reatomResource } from '@root/shared/lib'

import {
  fetchBlacklistTokensMetaRequestAction,
  hideTokenRequestAction,
  unhideTokenRequestAction,
} from './blacklist-requests'

import type { CurrencyModel } from '@root/entities/currency/model'

export const reatomBlacklistCurrenciesRemote = () => {
  const stateAtom = reatomResource(async (ctx) => {
    const isLoggedIn = ctx.spy(isLoggedInAtom)
    if (!isLoggedIn) {
      return Result.succeed([])
    }
    const network = ctx.spy(selectedNetworkAtom)
    const result = await ctx.schedule(() =>
      fetchBlacklistTokensMetaRequestAction(ctx, {
        network,
      }),
    )

    return result
  })

  const byAddressAtom = atom((ctx) => {
    const blacklistCurrencies = ctx.spy(stateAtom).value ?? []
    return pipe(
      blacklistCurrencies,
      Iterable.toMap((it) => it.ref.address),
    )
  })

  const hideAction = action(async (ctx, currency: CurrencyModel) => {
    const result = await ctx.schedule(() =>
      hideTokenRequestAction(ctx, {
        network: currency.network,
        tokenAddress: currency.address,
      }),
    )
    if (Result.isSuccess(result)) {
      const state = ctx.get(stateAtom)
      AsyncStateN.tap(state, (arr) => {
        if (arr.some((it) => CurrencyRef.eq(it.ref, currency))) {
          return
        }
        stateAtom.setSuccess(ctx, [
          {
            currency: currency,
            ref: { address: currency.address, network: currency.network },
            hiddenAt: new Date(),
          },
          ...arr,
        ])
      })
    }
    return result
  })

  const unhideAction = action(async (ctx, currency: CurrencyRef.Ref) => {
    const result = await ctx.schedule(() =>
      unhideTokenRequestAction(ctx, {
        network: currency.network,
        tokenAddress: currency.address,
      }),
    )
    if (Result.isSuccess(result)) {
      const state = ctx.get(stateAtom)
      AsyncStateN.tap(state, (arr) => {
        stateAtom.setSuccess(
          ctx,
          arr.filter((it) => !CurrencyRef.eq(it.ref, currency)),
        )
      })
    }
    return result
  })

  return stateAtom.pipe(
    withAssign(() => ({
      byAddress: byAddressAtom,
      unhide: unhideAction,
      hide: hideAction,
    })),
  )
}

export type BlacklistCurrenciesRemoteModel = ReturnType<
  typeof reatomBlacklistCurrenciesRemote
>
export const blacklistCurrenciesRemoteModel = reatomBlacklistCurrenciesRemote()
